import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { ConfirmDelete, PageHeader, Panel, StatusBadge } from '@/components/kit';
import { ConfirmAction } from '@/components/confirm';
import { BackLink, recordGate } from '@/components/record';
import { LineDialog, MessageDialog, TellyKeyDialog } from '@/components/admin/OrderActions';
import OrderFacts from '@/components/admin/OrderFacts';
import OrderPayments from '@/components/admin/OrderPayments';
import OrderLedger from '@/components/admin/OrderLedger';
import LedgerDialog from '@/components/admin/LedgerDialog';
import { createOrderEntry } from '@/shared/ledger';
import { ORDER_STATUS_STYLES } from '@/shared/constants';
import { ORDER_FIELDS } from '@/shared/fields';
import { TRIAL_HOURS, isLapsed, money, orderTotal, planSummary, tellyCount, termEnd, timeLeft } from '@/shared/pricing';
import { committed, logActivity } from '@/shared/data';
import { countDiscountUse, countReferral } from '@/shared/discounts';
import { GIFT_MONTHS_PER_REFERRAL, dropHandle, giftedEnd, syncHandle } from '@/shared/referrals';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';

export const route = { path: '/admin/orders/:id', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };

const MESSAGES = {
  Invoice: {
    kind: 'Invoice',
    title: 'Invoice message',
    description: 'Copy this into email or chat. Nothing is charged from here.',
  },
  Credentials: {
    kind: 'Credentials',
    title: 'Send line credentials',
    description: 'Filled in from the line details on this order.',
    stampField: 'credentials_sent_at_c',
  },
  Expiry: {
    kind: 'Expiry',
    title: 'Expiry reminder',
    description: 'A heads-up that the term is about to run out.',
    stampField: 'expiry_emailed_at_c',
  },
  Winback: {
    kind: 'Winback',
    title: 'Win-back offer',
    description: 'For a line that ended without a renewal.',
    stampField: 'winback_emailed_at_c',
  },
};

export default function AdminOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lineOpen, setLineOpen] = useState(false);
  const [tellyOpen, setTellyOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msg, setMsg] = useState(MESSAGES.Invoice);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledgerEntry, setLedgerEntry] = useState(null);
  const [ledgerKind, setLedgerKind] = useState('Activation');
  const [ledgerKey, setLedgerKey] = useState(0);

  const { data: order, loading, error, run } = useFetch(
    () => sdk.table('orders_c').select(ORDER_FIELDS).get(id).then((r) => r.data ?? null),
    [id],
  );

  const back = <BackLink to="/admin/orders" label="Back to Orders" />;
  const gate = recordGate({ back, loading, record: order, error, run, what: 'Order' });
  if (gate) return gate;

  const status = order.status_c;
  const waiting = status === 'Pending' || status === 'Invoiced';
  const live = status === 'Active' || status === 'Trial';
  const lapsed = isLapsed(order);
  const hasTelly = tellyCount(order) > 0;
  const left = timeLeft(order.ends_at_c);
  const owed = Number(order.gift_months_c || 0);
  const giftTo = giftedEnd(order);

  function openMessage(kind) {
    setMsg(MESSAGES[kind]);
    setMsgOpen(true);
  }

  async function update(fields, message, activity) {
    const res = await sdk.table('orders_c').update({ Id: order.Id, ...fields });
    if (!committed(res, message)) return false;
    if (activity) logActivity('Order', activity, order.Name, order.email_c);
    syncHandle(order);
    run();
    return true;
  }

  async function invoice() {
    const ok = await update({ status_c: 'Invoiced' }, 'Marked as invoiced', 'Invoiced order');
    if (ok) {
      countDiscountUse(order);
      openMessage('Invoice');
    }
    return ok;
  }

  async function startTrial() {
    const now = new Date();
    const ends = new Date(now.getTime() + TRIAL_HOURS * 3600000);
    const ok = await update(
      { status_c: 'Trial', activated_at_c: now.toISOString(), ends_at_c: ends.toISOString() },
      'Trial started',
      'Started a trial',
    );
    if (ok) countDiscountUse(order);
    return ok;
  }

  async function openLedgerFor(kind) {
    const entry = await createOrderEntry(order, kind);
    setLedgerKey((k) => k + 1);
    if (!entry) return;
    setLedgerKind(kind);
    setLedgerEntry(entry);
    setLedgerOpen(true);
  }

  async function activate() {
    const now = new Date();
    const ends = termEnd(order.plan_c, order.term_c, now);
    const ok = await update(
      {
        status_c: 'Active',
        activated_at_c: now.toISOString(),
        ends_at_c: ends?.toISOString() ?? null,
        renew_requested_at_c: null,
      },
      'Line activated',
      'Activated order',
    );
    if (ok) {
      countDiscountUse(order);
      countReferral(order);
      openLedgerFor('Activation');
    }
    return ok;
  }

  async function extend() {
    const now = new Date();
    const current = order.ends_at_c ? new Date(order.ends_at_c) : now;
    const from = current > now ? current : now;
    const ends = termEnd(order.plan_c, order.term_c, from);
    const ok = await update(
      {
        status_c: 'Active',
        ends_at_c: ends?.toISOString() ?? null,
        renew_requested_at_c: null,
        expiry_emailed_at_c: null,
        winback_emailed_at_c: null,
      },
      'Term extended',
      'Renewed order',
    );
    if (ok) openLedgerFor('Renewal');
    return ok;
  }

  async function giftMonth() {
    return update(
      {
        status_c: 'Active',
        ends_at_c: giftTo ? giftTo.toISOString() : order.ends_at_c,
        gift_months_c: Math.max(0, owed - GIFT_MONTHS_PER_REFERRAL),
        expiry_emailed_at_c: null,
        winback_emailed_at_c: null,
      },
      'Free month gifted',
      'Gifted a free referral month',
    );
  }

  async function markGifted() {
    return update(
      { gift_months_c: Math.max(0, owed - GIFT_MONTHS_PER_REFERRAL) },
      'Marked as gifted',
      'Marked a referral month as gifted',
    );
  }

  async function remove() {
    const res = await sdk.table('orders_c').remove(order.Id);
    if (!committed(res, 'Order deleted')) return false;
    dropHandle(order.Id);
    logActivity('Order', `Deleted ${order.Name}`, order.Name, order.email_c);
    navigate('/admin/orders');
    return true;
  }

  const nextEnd = termEnd(order.plan_c, order.term_c, new Date());

  return (
    <div className="mx-auto max-w-6xl">
      {back}
      <PageHeader
        eyebrow={`Order · placed ${formatLocalDate(order.CreatedOn, DATE_FORMATS.SHORT)}`}
        title={<span className="font-mono">{order.Name}</span>}
        description={`${planSummary(order)} · ${money(orderTotal(order))}${left ? ` · ${left}` : ''}`}
        actions={
          <>
            <StatusBadge value={status} styles={ORDER_STATUS_STYLES} />
            <Button variant="outline" size="sm" asChild>
              <Link to={`/admin/orders/${order.Id}/edit`}>
                <ApperIcon name="Pencil" size={16} />
                Edit
              </Link>
            </Button>
            <ConfirmDelete
              title={`Delete ${order.Name}?`}
              description="The order disappears from the customer's account too. Payments stay on record. This cannot be undone."
              onConfirm={remove}
            />
          </>
        }
      />

      {order.renew_requested_at_c && live && (
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-primary/40 bg-primary/10 px-4 py-3 text-sm">
          <ApperIcon name="RefreshCw" size={16} className="mt-0.5 text-primary" />
          <p>
            The customer asked to renew {formatLocalDate(order.renew_requested_at_c, DATE_FORMATS.SHORT)}. Invoice them, then
            extend the term once they pay.
          </p>
        </div>
      )}

      {owed > 0 && (
        <div className="mb-4 flex flex-col gap-3 rounded-lg border border-highlight/40 bg-highlight/10 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-start gap-3">
            <ApperIcon name="Gift" size={16} className="mt-0.5 shrink-0 text-highlight" />
            <span>
              <span className="font-semibold">
                {owed} free month{owed === 1 ? '' : 's'} owed
              </span>{' '}
              to this line for referring friends. Add the month on your panel, then record it here.
            </span>
          </p>
          <div className="flex shrink-0 flex-wrap gap-2">
            {giftTo && (
              <ConfirmAction
                label="Gift 1 month"
                icon="Gift"
                variant="default"
                title="Gift a free month?"
                description={`The line's end date moves to ${formatLocalDate(giftTo.toISOString(), DATE_FORMATS.LONG)} and the reminders reset. Remember to add the month on your panel too.`}
                busyLabel="Gifting…"
                onConfirm={giftMonth}
              />
            )}
            <ConfirmAction
              label="Already gifted"
              icon="Check"
              title="Mark one free month as gifted?"
              description="Use this when you added the month some other way. The end date here stays as it is."
              busyLabel="Saving…"
              onConfirm={markGifted}
            />
          </div>
        </div>
      )}

      <Panel title="Next steps" icon="Sparkles" className="mb-6" bodyClassName="flex flex-wrap gap-2 py-3">
        {status === 'Pending' && (
          <ConfirmAction
            label="Mark invoiced"
            icon="ReceiptText"
            variant="default"
            title="Mark this order as invoiced?"
            description="Then copy the invoice message and send it the way the customer asked to be contacted."
            busyLabel="Saving…"
            onConfirm={invoice}
          />
        )}
        {(status === 'Invoiced' || order.renew_requested_at_c) && (
          <Button variant="outline" size="sm" onClick={() => openMessage('Invoice')}>
            <ApperIcon name="ReceiptText" size={16} />
            Invoice message
          </Button>
        )}
        {waiting && (
          <ConfirmAction
            label={`Start ${TRIAL_HOURS}h trial`}
            icon="FlaskConical"
            title="Start a free trial?"
            description={`The line runs for ${TRIAL_HOURS} hours from now. Add the line details next.`}
            busyLabel="Starting…"
            onConfirm={startTrial}
          />
        )}
        {(waiting || status === 'Trial' || status === 'Cancelled') && (
          <ConfirmAction
            label="Activate"
            icon="Zap"
            variant={waiting || status === 'Trial' ? 'default' : 'outline'}
            title="Activate this line?"
            description={
              nextEnd
                ? `Payment received? The term starts now and runs to ${formatLocalDate(nextEnd.toISOString(), DATE_FORMATS.LONG)}.`
                : 'Payment received? A lifetime licence has no end date.'
            }
            busyLabel="Activating…"
            onConfirm={activate}
          />
        )}
        {status === 'Active' && (
          <ConfirmAction
            label="Extend term"
            icon="CalendarPlus"
            title="Extend by another term?"
            description="Adds one more term from the current end date (or from now if it already ended) and clears the reminders."
            busyLabel="Extending…"
            onConfirm={extend}
          />
        )}
        <Button variant="outline" size="sm" onClick={() => setLineOpen(true)}>
          <ApperIcon name="KeyRound" size={16} />
          Line details
        </Button>
        <Button variant="outline" size="sm" onClick={() => openMessage('Credentials')} disabled={!order.line_username_c}>
          <ApperIcon name="Send" size={16} />
          Send credentials
        </Button>
        {hasTelly && (
          <Button variant="outline" size="sm" onClick={() => setTellyOpen(true)}>
            <ApperIcon name="MonitorPlay" size={16} />
            Licence key
          </Button>
        )}
        {live && order.ends_at_c && (
          <Button variant="outline" size="sm" onClick={() => openMessage('Expiry')}>
            <ApperIcon name="Hourglass" size={16} />
            Expiry reminder
          </Button>
        )}
        {(lapsed || status === 'Cancelled') && (
          <Button variant="outline" size="sm" onClick={() => openMessage('Winback')}>
            <ApperIcon name="Gift" size={16} />
            Win-back offer
          </Button>
        )}
        {status === 'Cancelled' ? (
          <ConfirmAction
            label="Reopen"
            icon="RotateCcw"
            title="Reopen this order?"
            description="It goes back to Pending so you can invoice it again."
            busyLabel="Reopening…"
            onConfirm={() => update({ status_c: 'Pending' }, 'Order reopened', 'Reopened order')}
          />
        ) : (
          <ConfirmAction
            label="Cancel order"
            icon="Ban"
            destructive
            title="Cancel this order?"
            description="The line stops showing as active for the customer. You can reopen it later."
            confirmLabel="Cancel order"
            busyLabel="Cancelling…"
            onConfirm={() => update({ status_c: 'Cancelled' }, 'Order cancelled', 'Cancelled order')}
          />
        )}
      </Panel>

      <OrderFacts order={order} refreshKey={ledgerKey} onCostsSaved={() => setLedgerKey((k) => k + 1)} />

      <div className="mt-6">
        <OrderPayments order={order} />
      </div>

      <div className="mt-6">
        <OrderLedger order={order} refreshKey={ledgerKey} />
      </div>

      <LedgerDialog
        open={ledgerOpen}
        onOpenChange={setLedgerOpen}
        entry={ledgerEntry}
        order={order}
        title={ledgerKind === 'Renewal' ? 'Log credits for this renewal' : 'Log credits for this activation'}
        description="Type what this line cost you and what you collected. Skip it now and it waits in the Ledger as Needs numbers."
        onSaved={() => setLedgerKey((k) => k + 1)}
      />

      <LineDialog open={lineOpen} onOpenChange={setLineOpen} order={order} onSaved={run} />
      <TellyKeyDialog open={tellyOpen} onOpenChange={setTellyOpen} order={order} onSaved={run} />
      <MessageDialog
        open={msgOpen}
        onOpenChange={setMsgOpen}
        order={order}
        kind={msg.kind}
        title={msg.title}
        description={msg.description}
        stampField={msg.stampField}
        onSent={run}
      />
    </div>
  );
}
